import React, { useEffect } from 'react';
import { CheckCircle2, ArrowRight, Calendar, Mail, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import ApexNavbar from '../components/apex/ApexNavbar';
import ApexFooter from '../components/apex/ApexFooter';

const ThankYou = () => {
  useEffect(() => {
    // Track successful booking for Meta Pixel
    if (window.fbq) {
      window.fbq('track', 'Schedule', { content_name: 'Sales Audit Booked' });
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <ApexNavbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[80vh] flex items-center overflow-hidden">
          {/* Background effects */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A] to-[#0d1f12]" />
          <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-[#22C55E]/15 rounded-full blur-[180px]" />
          <div className="absolute bottom-1/4 left-1/4 w-[400px] h-[400px] bg-[#22C55E]/10 rounded-full blur-[150px]" />
          
          <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-20 text-center">
            {/* Success Icon */}
            <div className="mb-8">
              <div className="w-24 h-24 rounded-full bg-[#22C55E]/20 flex items-center justify-center mx-auto animate-pulse">
                <CheckCircle2 className="w-14 h-14 text-[#22C55E]" />
              </div>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6">
              YOUR AUDIT IS{' '}
              <span className="text-[#22C55E]">BOOKED!</span>
            </h1>

            {/* Confirmation message */}
            <p className="text-xl md:text-2xl text-[#A1A1AA] leading-relaxed max-w-2xl mx-auto mb-4">
              Thank you for taking the first step towards transforming your sales process.
            </p>
            <p className="text-lg text-white font-medium max-w-2xl mx-auto mb-12">
              Check your email for the calendar invite and meeting details.
            </p>

            {/* What to expect box */}
            <div className="bg-[#121212] border border-[#27272A] rounded-2xl p-8 md:p-10 max-w-2xl mx-auto mb-12">
              <h2 className="text-xl font-bold text-white mb-6">
                WHAT TO EXPECT ON YOUR CALL
              </h2>
              
              <div className="space-y-4 text-left">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#22C55E]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#22C55E] font-bold text-sm">1</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold">Quick Business Overview</p>
                    <p className="text-[#A1A1AA] text-sm">We will review the details you provided in your application</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#22C55E]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#22C55E] font-bold text-sm">2</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold">Sales Process Audit</p>
                    <p className="text-[#A1A1AA] text-sm">We will identify exactly where revenue is leaking in your current process</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#22C55E]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#22C55E] font-bold text-sm">3</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold">£5K Qualification</p>
                    <p className="text-[#A1A1AA] text-sm">We will determine whether your business qualifies for the £5K Guarantee</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#22C55E]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[#22C55E] font-bold text-sm">4</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold">Action Plan</p>
                    <p className="text-[#A1A1AA] text-sm">You will leave with clear next steps regardless of whether you join the programme</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Preparation tips */}
            <div className="bg-gradient-to-r from-[#121212] via-[#0d1f12] to-[#121212] border border-[#22C55E]/30 rounded-2xl p-8 max-w-2xl mx-auto mb-12">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Calendar className="w-6 h-6 text-[#22C55E]" />
                <h3 className="text-lg font-bold text-white">BEFORE YOUR CALL</h3>
              </div>
              <p className="text-[#A1A1AA] mb-4">
                To get the most out of your audit, have these numbers ready:
              </p>
              <ul className="text-white text-sm space-y-2">
                <li className="flex items-center gap-2">
                  <span className="text-[#22C55E]">•</span>
                  Your current monthly revenue
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#22C55E]">•</span>
                  Number of leads you generate per month
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#22C55E]">•</span>
                  Your current close rate (if you know it)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-[#22C55E]">•</span>
                  Average membership/programme value
                </li>
              </ul>
            </div>

            {/* Contact info */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12">
              <div className="flex items-center gap-2 text-[#A1A1AA]">
                <Mail className="w-5 h-5 text-[#22C55E]" />
                <a href="mailto:kyran@apexfitsalescoaching.com" className="hover:text-white transition-colors">
                  kyran@apexfitsalescoaching.com
                </a>
              </div>
            </div>

            {/* Back to home */}
            <Link
              to="/"
              className="group inline-flex items-center justify-center gap-2 bg-transparent border border-[#27272A] hover:border-[#22C55E] text-white hover:text-[#22C55E] font-bold rounded-full px-8 py-4 transition-all duration-300"
            >
              Back to Homepage
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </section>
      </main>

      <ApexFooter />
    </div>
  );
};

export default ThankYou;
